import { istEntwurfsKennung, istUnveraenderlich } from '@uep/shared';
import { api, fehlerText, istDauerhafterFehler, pruefeAntwort } from './api';
import { db, objektSuchtext } from './db';
import { dokumentSchluessel, hochladen } from './s3';
import {
  alsErledigt,
  alsFehlgeschlagen,
  alsLaufend,
  gruppeBlockiert,
  naechsteEintraege,
} from './outbox';
import type { OutboxEintrag } from './typen';

/**
 * Outbox abarbeiten (PHASENPLAN 1.2/1.3/1.6).
 *
 * Idempotenz durch Konstruktion (CLAUDE.md §3.4): IDs werden clientseitig
 * erzeugt, `anlegen` fällt bei einem Duplikat auf `aendern` zurück, `aendern`
 * schreibt denselben Zielzustand, `loeschen` ist wiederholbar. Nur der
 * Protokollabschluss vergibt eine laufende Nummer und braucht deshalb einen
 * echten Idempotenzschlüssel — den trägt der Outbox-Eintrag als `id`.
 */

export interface PushErgebnis {
  gesendet: number;
  fehlgeschlagen: number;
  aufgegeben: number;
}

/**
 * Erkennt eine fehlgeschlagene Bedingungsprüfung von DynamoDB.
 *
 * Amplify schreibt `create` mit `attribute_not_exists(id)` und `update` mit
 * `attribute_exists(id)`. Passt die Bedingung nicht, meldet AppSync das als
 * „The conditional request failed“ — **ohne** den Namen
 * `ConditionalCheckFailedException`. Genau daran ging die Prüfung vorher vorbei:
 * `anlegen` fiel nicht auf `aendern` zurück, der Eintrag blieb in der
 * Warteschlange und wurde endlos wiederholt („Sendeversuch fehlgeschlagen“,
 * obwohl das Gerät online war).
 */
const istBedingungsFehler = (fehler: unknown): boolean => {
  const text = fehlerText(fehler).toLowerCase();
  return (
    text.includes('conditionalcheckfailed') ||
    text.includes('conditional request failed') ||
    text.includes('conditional check failed') ||
    text.includes('already exists') ||
    // Wortlaut von AppSync, wenn `update` einen fehlenden Datensatz trifft.
    text.includes('does not exist')
  );
};

/**
 * Schreibt idempotent (CLAUDE.md §3.4).
 *
 * `anlegen` fällt bei einem bereits vorhandenen Datensatz auf `aendern` zurück,
 * `aendern` bei einem serverseitig fehlenden auf `anlegen`. Beide Richtungen
 * kommen im Alltag vor: Die Antwort auf ein erfolgreiches `create` geht im
 * Funkloch verloren, oder ein Gerät ändert einen Datensatz, dessen `anlegen`
 * noch in einer anderen Warteschlange steckt.
 */
const schreibeIdempotent = async (
  operation: OutboxEintrag['operation'],
  anlegen: () => Promise<unknown>,
  aendern: () => Promise<unknown>,
): Promise<void> => {
  const [erster, zweiter] = operation === 'anlegen' ? [anlegen, aendern] : [aendern, anlegen];
  try {
    await erster();
  } catch (fehler) {
    if (!istBedingungsFehler(fehler)) throw fehler;
    await zweiter();
  }
};

// ---------------------------------------------------------------------------
// Einzelne Operationen
// ---------------------------------------------------------------------------

const sendeProtokoll = async (eintrag: OutboxEintrag): Promise<void> => {
  const protokoll = await db.protokolle.get(eintrag.entitaetId);
  if (!protokoll || protokoll.konfliktkopie === 1) return;

  /*
   * Ein lokal abgeschlossenes Protokoll trägt bis zur Antwort des Servers noch
   * die Entwurfskennung als Nummer — die echte vergibt erst der Abschluss.
   *
   * Dieser Zwischenzustand darf nicht als Endzustand gesendet werden: Der
   * Server sah dann ein bereits abgeschlossenes Protokoll, `protokollAbschliessen`
   * antwortete „war bereits abgeschlossen", vergab keine Nummer und schrieb die
   * Unterschriften nicht. Das PDF trug dauerhaft „Entwurf". Deshalb geht hier
   * `DRAFT` raus; den Endstatus setzt allein der Abschluss, der in derselben
   * Gruppe unmittelbar folgt.
   */
  const abschlussOffen =
    istUnveraenderlich(protokoll.status) && istEntwurfsKennung(protokoll.protokollNr);

  const nutzlast = {
    id: protokoll.id,
    objektId: protokoll.objektId,
    einheitId: protokoll.einheitId,
    templateVersionId: protokoll.templateVersionId,
    protokollNr: protokoll.protokollNr,
    status: abschlussOffen ? ('DRAFT' as const) : protokoll.status,
    art: protokoll.art,
    uebergabeDatum: protokoll.uebergabeDatum,
    data: JSON.stringify(protokoll.data),
    beteiligte: JSON.stringify(protokoll.beteiligte),
    dataHash: protokoll.dataHash,
    vorgaengerId: protokoll.vorgaengerId,
    vorprotokollId: protokoll.vorprotokollId,
    erfasstVon: protokoll.erfasstVon,
    geraeteId: protokoll.geraeteId,
    aufbewahrungBis: protokoll.aufbewahrungBis,
    revision: protokoll.revision,
  };

  await schreibeIdempotent(
    eintrag.operation,
    async () => pruefeAntwort(await api().models.Protocol.create(nutzlast)),
    async () => pruefeAntwort(await api().models.Protocol.update(nutzlast)),
  );

  await db.protokolle.update(protokoll.id, { synchronisiert: 1 });
};

const sendeObjekt = async (eintrag: OutboxEintrag): Promise<void> => {
  const objekt = await db.objekte.get(eintrag.entitaetId);
  if (!objekt) return;

  const nutzlast = {
    id: objekt.id,
    objektNr: objekt.objektNr,
    bezeichnung: objekt.bezeichnung,
    strasse: objekt.strasse,
    plz: objekt.plz,
    ort: objekt.ort,
    lage: objekt.lage,
    wohnflaeche: objekt.wohnflaeche,
    zimmeranzahl: objekt.zimmeranzahl,
    eigentuemer: objekt.eigentuemer,
    notiz: objekt.notiz,
    aktiv: objekt.aktiv === 1,
    schluessel: objekt.schluessel ? JSON.stringify(objekt.schluessel) : null,
    suchtext: objektSuchtext(objekt),
  };

  await schreibeIdempotent(
    eintrag.operation,
    async () => pruefeAntwort(await api().models.Objekt.create(nutzlast)),
    async () => pruefeAntwort(await api().models.Objekt.update(nutzlast)),
  );

  await db.objekte.update(objekt.id, { synchronisiert: 1 });
};

const sendeEinheit = async (eintrag: OutboxEintrag): Promise<void> => {
  if (eintrag.operation === 'loeschen') {
    await api().models.Einheit.delete({ id: eintrag.entitaetId });
    await db.einheiten.delete(eintrag.entitaetId);
    return;
  }

  const einheit = await db.einheiten.get(eintrag.entitaetId);
  if (!einheit) return;

  const nutzlast = {
    id: einheit.id,
    objektId: einheit.objektId,
    bezeichnung: einheit.bezeichnung,
    einheitNr: einheit.einheitNr,
    art: (einheit.art ?? null) as 'WOHNUNG' | 'GEWERBE' | 'STELLPLATZ' | 'SONSTIGE' | null,
    etage: einheit.etage,
    lage: einheit.lage,
    wohnflaeche: einheit.wohnflaeche,
    zimmeranzahl: einheit.zimmeranzahl,
    reihenfolge: einheit.reihenfolge,
    notiz: einheit.notiz,
    aktiv: einheit.aktiv === 1,
    schluessel: einheit.schluessel ? JSON.stringify(einheit.schluessel) : null,
  };

  await schreibeIdempotent(
    eintrag.operation,
    async () => pruefeAntwort(await api().models.Einheit.create(nutzlast)),
    async () => pruefeAntwort(await api().models.Einheit.update(nutzlast)),
  );

  await db.einheiten.update(einheit.id, { synchronisiert: 1 });
};

const sendeRaum = async (eintrag: OutboxEintrag): Promise<void> => {
  if (eintrag.operation === 'loeschen') {
    await api().models.Raum.delete({ id: eintrag.entitaetId });
    await db.raeume.delete(eintrag.entitaetId);
    return;
  }

  const raum = await db.raeume.get(eintrag.entitaetId);
  if (!raum) return;

  const nutzlast = {
    id: raum.id,
    objektId: raum.objektId,
    einheitId: raum.einheitId,
    name: raum.name,
    flaeche: raum.flaeche,
    reihenfolge: raum.reihenfolge,
    boden: raum.boden,
    wand: raum.wand,
    decke: raum.decke,
    ausstattung: raum.ausstattung ? JSON.stringify(raum.ausstattung) : null,
  };

  await schreibeIdempotent(
    eintrag.operation,
    async () => pruefeAntwort(await api().models.Raum.create(nutzlast)),
    async () => pruefeAntwort(await api().models.Raum.update(nutzlast)),
  );

  await db.raeume.update(raum.id, { synchronisiert: 1 });
};

const sendeDokument = async (eintrag: OutboxEintrag): Promise<void> => {
  if (eintrag.operation === 'loeschen') {
    await api().models.Document.delete({ id: eintrag.entitaetId });
    await db.dokumente.delete(eintrag.entitaetId);
    return;
  }

  const dokument = await db.dokumente.get(eintrag.entitaetId);
  if (!dokument) return;

  const nutzlast = {
    id: dokument.id,
    objektId: dokument.objektId,
    protocolId: dokument.protokollId,
    typ: dokument.typ as 'PROTOKOLL' | 'FOTO' | 'NOTIZ' | 'VERTRAG' | 'RECHNUNG' | 'SONSTIGE',
    titel: dokument.titel,
    beschreibung: dokument.beschreibung,
    s3Key: dokument.s3Key,
    mimeType: dokument.mimeType,
    groesseBytes: dokument.groesseBytes,
    aufgenommenAm: dokument.aufgenommenAm,
    geoLat: dokument.geoLat,
    geoLng: dokument.geoLng,
    tags: dokument.tags,
    inhalt: dokument.inhalt,
  };

  await schreibeIdempotent(
    eintrag.operation,
    async () => pruefeAntwort(await api().models.Document.create(nutzlast)),
    async () => pruefeAntwort(await api().models.Document.update(nutzlast)),
  );

  await db.dokumente.update(dokument.id, { synchronisiert: 1 });
};

/**
 * Foto hochladen (PHASENPLAN 1.6).
 *
 * Der Zustand des Multipart-Uploads wird nach jedem Teil im Fotodatensatz
 * gesichert. Bricht die App ab, macht der nächste Versuch beim fehlenden Teil
 * weiter statt von vorn.
 */
const sendeFoto = async (eintrag: OutboxEintrag): Promise<void> => {
  const foto = await db.fotos.get(eintrag.entitaetId);
  if (!foto) return;
  if (foto.hochgeladen === 1 && foto.s3Key) return;

  const dateiname = `${foto.id}.jpg`;
  const key = foto.s3Key ?? dokumentSchluessel(foto.objektId, foto.id, dateiname);

  await hochladen({
    key,
    daten: foto.blob,
    mimeType: foto.mimeType,
    zustand: foto.uploadId
      ? {
          uploadId: foto.uploadId,
          teile: (foto.hochgeladeneTeile ?? []).map((teil) => ({
            PartNumber: teil.teil,
            ETag: teil.etag,
          })),
        }
      : null,
    aufZustand: async (zustand) => {
      await db.fotos.update(foto.id, {
        uploadId: zustand.uploadId,
        hochgeladeneTeile: zustand.teile.map((teil) => ({
          teil: teil.PartNumber ?? 0,
          etag: teil.ETag ?? '',
        })),
      });
    },
  });

  // Das Foto ist zugleich ein Dokument in der Objektakte.
  const dokumentNutzlast = {
    id: foto.id,
    objektId: foto.objektId,
    protocolId: foto.protokollId,
    typ: 'FOTO' as const,
    titel: foto.beschriftung ?? `Foto vom ${new Date(foto.erstelltAm).toLocaleDateString('de-DE')}`,
    s3Key: key,
    mimeType: foto.mimeType,
    groesseBytes: foto.groesseBytes,
    aufgenommenAm: foto.aufgenommenAm,
    geoLat: foto.geoLat,
    geoLng: foto.geoLng,
    tags: [] as string[],
  };

  await schreibeIdempotent(
    'anlegen',
    async () => pruefeAntwort(await api().models.Document.create(dokumentNutzlast)),
    async () => pruefeAntwort(await api().models.Document.update(dokumentNutzlast)),
  );

  await db.fotos.update(foto.id, {
    hochgeladen: 1,
    s3Key: key,
    uploadId: null,
    hochgeladeneTeile: null,
  });
};

/**
 * Protokollabschluss (PHASENPLAN 4.7/4.8).
 *
 * Die einzige nicht von Natur aus idempotente Operation — deshalb geht die
 * Outbox-`id` als Idempotenzschlüssel mit. Ein Retry liefert dieselbe
 * Protokollnummer zurück, nie eine zweite.
 */
const sendeAbschluss = async (eintrag: OutboxEintrag): Promise<void> => {
  const protokoll = await db.protokolle.get(eintrag.entitaetId);
  if (!protokoll) return;

  const signaturen = await db.signaturen.where('protokollId').equals(protokoll.id).toArray();

  const antwort = pruefeAntwort(
    await api().mutations.protokollAbschliessen({
      protocolId: protokoll.id,
      idempotencyKey: eintrag.id,
      dataHash: protokoll.dataHash ?? '',
      signaturenJson: JSON.stringify(
        signaturen.map((signatur) => ({
          id: signatur.id,
          rolle: signatur.rolle,
          name: signatur.name,
          pfadSvg: signatur.pfadSvg,
          breite: signatur.breite,
          hoehe: signatur.hoehe,
          signiertAm: signatur.signiertAm,
          dataHash: signatur.dataHash,
          geraeteId: signatur.geraeteId,
        })),
      ),
      geraeteId: protokoll.geraeteId,
    }),
  );

  if (!antwort.erfolg) {
    // Fachliche Ablehnung (z. B. fehlende Unterschrift) — ein Retry hilft nicht.
    const meldung = antwort.meldung ?? 'Der Abschluss wurde abgelehnt.';
    const fehler = new Error(meldung);
    fehler.name = 'AbschlussAbgelehnt';
    throw fehler;
  }

  await db.protokolle.update(protokoll.id, {
    protokollNr: antwort.protokollNr ?? protokoll.protokollNr,
    status: (antwort.status as typeof protokoll.status) ?? 'SIGNED',
    synchronisiert: 1,
  });
  await db.signaturen.where('protokollId').equals(protokoll.id).modify({ synchronisiert: 1 });
};

const sendeTermin = async (eintrag: OutboxEintrag): Promise<void> => {
  if (eintrag.operation === 'loeschen') {
    await api().models.Termin.delete({ id: eintrag.entitaetId });
    await db.termine.delete(eintrag.entitaetId);
    return;
  }

  const termin = await db.termine.get(eintrag.entitaetId);
  if (!termin) return;

  const nutzlast = {
    id: termin.id,
    objektId: termin.objektId,
    protocolId: termin.protokollId,
    titel: termin.titel,
    art: termin.art,
    beginn: termin.beginn,
    ende: termin.ende,
    ort: termin.ort,
    notiz: termin.notiz,
    teilnehmer: termin.teilnehmer ? JSON.stringify(termin.teilnehmer) : null,
    zugewiesenAn: termin.zugewiesenAn,
    erledigt: termin.erledigt === 1,
  };

  await schreibeIdempotent(
    eintrag.operation,
    async () => pruefeAntwort(await api().models.Termin.create(nutzlast)),
    async () => pruefeAntwort(await api().models.Termin.update(nutzlast)),
  );

  await db.termine.update(termin.id, { synchronisiert: 1 });
};

/**
 * Persönliches Profil.
 *
 * Der Server kennt den Datensatz unter der Cognito-`sub`; `owner` setzt AppSync
 * selbst aus dem Token, deshalb steht das Feld hier nicht in der Nutzlast.
 */
const sendeProfil = async (eintrag: OutboxEintrag): Promise<void> => {
  const profil = await db.profile.get(eintrag.entitaetId);
  if (!profil) return;

  const nutzlast = {
    id: profil.id,
    anzeigename: profil.anzeigename,
    funktion: profil.funktion,
    firma: profil.firma,
    telefon: profil.telefon,
    email: profil.email,
    standardRolle: profil.standardRolle,
    alsBeteiligterEintragen: profil.alsBeteiligterEintragen === 1,
    erhaeltProtokoll: profil.erhaeltProtokoll === 1,
  };

  await schreibeIdempotent(
    eintrag.operation,
    async () => pruefeAntwort(await api().models.Profil.create(nutzlast)),
    async () => pruefeAntwort(await api().models.Profil.update(nutzlast)),
  );

  await db.profile.update(profil.id, { synchronisiert: 1 });
};

const sendeAudit = async (eintrag: OutboxEintrag): Promise<void> => {
  const nutzlast = eintrag.nutzlast as {
    aktion: string;
    entitaet?: string;
    entitaetId?: string;
    akteur?: string;
    details?: Record<string, unknown>;
    zeitpunkt?: string;
  };

  pruefeAntwort(
    await api().models.AuditLog.create({
      zeitpunkt: nutzlast.zeitpunkt ?? eintrag.erstelltAm,
      aktion: nutzlast.aktion,
      entitaet: nutzlast.entitaet ?? null,
      entitaetId: nutzlast.entitaetId ?? null,
      akteur: nutzlast.akteur ?? null,
      details: nutzlast.details ? JSON.stringify(nutzlast.details) : null,
    }),
  );
};

// ---------------------------------------------------------------------------
// Abarbeitung
// ---------------------------------------------------------------------------

const verarbeite = async (eintrag: OutboxEintrag): Promise<void> => {
  switch (eintrag.entitaet) {
    case 'Protocol':
      if (eintrag.operation === 'abschliessen') return sendeAbschluss(eintrag);
      return sendeProtokoll(eintrag);
    case 'Objekt':
      return sendeObjekt(eintrag);
    case 'Einheit':
      return sendeEinheit(eintrag);
    case 'Raum':
      return sendeRaum(eintrag);
    case 'Document':
      return sendeDokument(eintrag);
    case 'Foto':
      return sendeFoto(eintrag);
    case 'Termin':
      return sendeTermin(eintrag);
    case 'Profil':
      return sendeProfil(eintrag);
    case 'AuditLog':
      return sendeAudit(eintrag);
    default:
      throw new Error(`Unbekannte Entität in der Outbox: ${String(eintrag.entitaet)}`);
  }
};

/**
 * Übersetzt Fehler, die auf ein veraltetes Backend hindeuten.
 *
 * Ist die App neuer als das ausgerollte Backend, fehlt dort ein Modell oder ein
 * Feld. Der Rohtext lautet dann „Cannot read properties of undefined" oder
 * „Cannot query field …" — daraus liest niemand ab, dass ein Deploy fehlt.
 * Wiederholen hilft in diesem Fall nie, deshalb gilt der Fehler als dauerhaft.
 */
const BACKEND_VERALTET =
  /cannot read properties of undefined|is not a function|cannot query field|unknown field|unknown type|unknown argument/i;

export const istBackendVeraltet = (text: string): boolean => BACKEND_VERALTET.test(text);

export const gedeuteterFehler = (entitaet: string, text: string): string => {
  if (istBackendVeraltet(text)) {
    return (
      `Das Backend ist älter als diese App-Version — „${entitaet}" ist dort noch nicht bekannt. ` +
      `Bitte das Backend erneut ausrollen; erst danach kann dieser Eintrag gesendet werden. (${text})`
    );
  }
  // Hier landet nur, was auch über den Gegenweg (anlegen ⇄ ändern) nicht
  // durchging — dann steht der Datensatz auf dem Server wirklich anders da.
  if (istBedingungsFehler(text)) {
    return (
      `Der Server hat die Änderung an „${entitaet}" abgelehnt: Der Datensatz liegt dort in einem ` +
      `anderen Zustand — vermutlich wurde er zwischenzeitlich gelöscht oder von einem anderen Gerät ` +
      `geändert. Bitte die Administration informieren; die lokalen Daten bleiben erhalten. (${text})`
    );
  }
  return text;
};

export const pushAusfuehren = async (maxProDurchlauf = 25): Promise<PushErgebnis> => {
  let gesendet = 0;
  let fehlgeschlagen = 0;
  let aufgegeben = 0;

  for (let runde = 0; runde < maxProDurchlauf; runde += 1) {
    const eintraege = await naechsteEintraege(6);
    if (eintraege.length === 0) break;

    for (const eintrag of eintraege) {
      if (await gruppeBlockiert(eintrag)) continue;

      await alsLaufend(eintrag.id);
      try {
        await verarbeite(eintrag);
        await alsErledigt(eintrag.id);
        gesendet += 1;
      } catch (fehler) {
        const text = fehlerText(fehler);
        const dauerhaft =
          istDauerhafterFehler(fehler) ||
          istBackendVeraltet(text) ||
          (fehler instanceof Error && fehler.name === 'AbschlussAbgelehnt');
        await alsFehlgeschlagen(eintrag, gedeuteterFehler(eintrag.entitaet, text), dauerhaft);
        if (dauerhaft) aufgegeben += 1;
        else fehlgeschlagen += 1;
        // Nach einem Fehler in einer Gruppe geht es dort erst nach dem Backoff
        // weiter — andere Gruppen laufen ungehindert weiter.
      }
    }

    if (gesendet === 0 && (fehlgeschlagen > 0 || aufgegeben > 0)) break;
  }

  return { gesendet, fehlgeschlagen, aufgegeben };
};
